/**
 * Results Renderer
 * Render search results list with heading, cards, and tracking
 */

import { state } from '../core/state.js';
import { elements } from '../core/elements.js';
import { renderCampCard } from './camp-card.js';
import { renderLoadMoreButton } from './load-more.js';
import { getSportNamesBySlugs } from '../utils/sport-utils.js';
import { hasSpellingMistakes } from '../decision-engine/location-comparison.js';
import { readUserGeoFromCache } from '../decision-engine/user-geo.js';

// Will be injected by index.js
let updateActiveFilters = null;
let pushGTMViewItemList = null;
let selectItemTracking = null;

/**
 * Set dependencies
 */
export function setDependencies(deps) {
  updateActiveFilters = deps.updateActiveFilters;
  pushGTMViewItemList = deps.pushGTMViewItemList;
  selectItemTracking = deps.selectItemTracking;
}

/**
 * Render search results
 * Handles heading, cards, Load More button, and analytics tracking
 * @param {Array} hits - Array of camp objects to display
 * @param {number} nbHits - Total number of hits available
 * @param {boolean} append - If true, append to existing results (for Load More)
 * @param {string|null} labelTemplate - Optional label template for heading
 * @param {string|null} queryID - Algolia query ID for tracking
 */
export function renderResults(
  hits,
  nbHits,
  append = false,
  labelTemplate = null,
  queryID = null
) {
  console.log('renderResults called:', {
    hitsLength: hits.length,
    nbHits,
    append,
    labelTemplate,
  });

  // Update results count and heading only on initial render (not on Load More)
  if (!append) {
    // Persist the EXACT center the DISPLAYED cards measured distance from, so the camp
    // page shows the SAME number. Done HERE — the single final render of every search
    // path — so a later centerless decision-flow step can't wipe a valid center. Uses
    // the exact same showDistance rule as renderCampCard (set iff cards show distance).
    try {
      const _userGeo = readUserGeoFromCache();
      const _hasDates = !!(state.beginDates && state.endDates);
      const _hasLocation = !!(state.zip || '').trim();
      const _isNonUS = _userGeo?.isUS === false;
      const _shouldHideDistance = _isNonUS && (_hasDates || !_hasLocation);
      const _showDistance = _shouldHideDistance ? false : !!state.showDistance;
      const _c = state.coordinates;
      if (_showDistance && _c && _c.lat != null && _c.lng != null) {
        localStorage.setItem(
          'campSearchCenter',
          JSON.stringify({ lat: _c.lat, lng: _c.lng })
        );
      } else {
        localStorage.removeItem('campSearchCenter');
      }
    } catch (e) {
      /* non-fatal: camp page just falls back to realLocationData */
    }

    // ── DIAGNOSTIC (search-distance) — full snapshot to reproduce distance bugs ──
    // One log per search render: the center the CARDS measured from (aroundLatLng),
    // the center persisted for the camp-page PILL, the raw IP/geocode cache, and the
    // first few card distances. Pair with the camp page's "📏 [ab_camp_distance]" log.
    try {
      const _cacheKey = window.CampsUtils?.defaults?.cacheKey;
      let _cache = null;
      try {
        _cache = JSON.parse(localStorage.getItem(_cacheKey) || 'null');
      } catch (e) {}
      let _persistedCenter = null;
      try {
        _persistedCenter = JSON.parse(
          localStorage.getItem('campSearchCenter') || 'null'
        );
      } catch (e) {}

      const _coords = _cache?.coords || null;
      const _real = _cache?.realLocationData || null;
      const _center = state.coordinates || null;
      const _userGeoD = readUserGeoFromCache();
      const _hasDatesD = !!(state.beginDates && state.endDates);
      const _hasLocD = !!(state.zip || '').trim();
      const _showDistD =
        _userGeoD?.isUS === false && (_hasDatesD || !_hasLocD)
          ? false
          : !!state.showDistance;

      const _same = (a, b) =>
        !!(
          a &&
          b &&
          a.lat != null &&
          b.lat != null &&
          Math.abs(a.lat - b.lat) < 1e-5 &&
          Math.abs(a.lng - b.lng) < 1e-5
        );

      const _sampleCards = (hits || []).slice(0, 5).map((c) => {
        const g = Array.isArray(c._geoloc) ? c._geoloc[0] : c._geoloc;
        const gd = c._rankingInfo?.geoDistance;
        return {
          title: c.title,
          city: c.address?.map?.city || c.address?.city || '',
          geoDistance_m: gd ?? null,
          card_mi: gd != null ? (gd / 1609).toFixed(1) : null,
          geoloc: g ? { lat: g.lat, lng: g.lng } : null,
          geolocPoints: Array.isArray(c._geoloc)
            ? c._geoloc.length
            : c._geoloc
            ? 1
            : 0,
        };
      });

      console.log('📊 [search-distance] search render debug', {
        query: state.zip || '(empty / near me)',
        isDefaultIpLocation: state.isDefaultIpLocation,
        isStateSearch: state.isStateSearch || false,
        searchStateCode: state.searchStateCode || null,
        sort: state.sort,
        showDistance: _showDistD,
        nbHits,
        displayed: hits?.length ?? 0,
        searchCenter_aroundLatLng: _center,
        campSearchCenter_persisted: _persistedCenter,
        centerSource: _same(_center, _real)
          ? 'realLocationData (IP) ✅'
          : _same(_center, _coords)
          ? 'coords (geocoded centroid)'
          : 'other',
        coords_vs_realIP:
          _real && _coords
            ? _same(_coords, _real)
              ? 'equal'
              : 'DIFFER (coords drifted from IP)'
            : 'n/a',
        cache: {
          userLocation: _cache?.userLocation || null,
          city: _cache?.city || null,
          regionCode: _cache?.regionCode || null,
          coords: _coords
            ? {
                lat: _coords.lat,
                lng: _coords.lng,
                postal: _coords.postal,
                isUS: _coords.isUS,
              }
            : null,
          realLocationData: _real
            ? {
                lat: _real.lat,
                lng: _real.lng,
                postal: _real.postal,
                isUS: _real.isUS,
                regionCode: _real.regionCode || _real.region,
              }
            : null,
        },
        sampleCards: _sampleCards,
      });
    } catch (e) {
      /* non-fatal diagnostic */
    }

    // Update results count
    if (elements.resultsCount) {
      elements.resultsCount.textContent = `(${nbHits} camps)`;
    }

    // Show/hide results heading
    if (elements.resultsHeading) {
      updateResultsHeading(hits, nbHits, labelTemplate);
    }
  }

  // Render camp cards
  if (elements.resultsContainer) {
    console.log('Results container found, append mode:', append);

    // Clear container only on initial render, not on Load More
    if (!append) {
      console.log('Clearing container for new search');
      elements.resultsContainer.innerHTML = '';

      // Also clear found-camp-block from dual search (it's outside resultsContainer)
      // BUT only if we're NOT in dual search mode (otherwise we'd delete the block we just created)
      if (!state.foundCampObjectID) {
        const foundCampBlock = document.querySelector(
          '.rd-search-results__found-camp-block'
        );
        const campMatchLabel = document.querySelector(
          '.rd-search-results__camp-match-title'
        );
        if (foundCampBlock) foundCampBlock.remove();
        if (campMatchLabel) campMatchLabel.remove();
      }
    } else {
      console.log('Appending to existing results');
    }

    if (hits.length === 0 && !append) {
      elements.noResults && (elements.noResults.style.display = 'block');
    } else {
      elements.noResults && (elements.noResults.style.display = 'none');
    }

    hits.forEach((camp, index) => {
      // renderCampCard returns HTML with <li> included
      elements.resultsContainer.insertAdjacentHTML(
        'beforeend',
        renderCampCard(camp)
      );
    });

    // Add click tracking to camp card links
    if (queryID && window.AlgoliaAnalyticsHandlers) {
      setTimeout(() => {
        const campLinks = elements.resultsContainer.querySelectorAll(
          '.rd-camp-card__link, .media.tile a'
        );
        campLinks.forEach((link, index) => {
          // Skip if already has click listener
          if (link.dataset.algoliaTracked === 'true') return;

          const campCard = link.closest('.rd-camp-card, li');
          const campId = campCard?.getAttribute('data-camp-id');

          // Find camp by id or objectID
          const camp = hits.find((h) => {
            const hId = String(h.id || '');
            const hObjectId = String(h.objectID || '');
            const searchId = String(campId || '');
            return hId === searchId || hObjectId === searchId;
          });

          const objectID = camp?.objectID || camp?.id;

          if (camp && objectID) {
            // Calculate position in displayed results
            const position = append
              ? state.displayedHits - hits.length + index + 1
              : index + 1;

            link.addEventListener('click', function (e) {
              window.AlgoliaAnalyticsHandlers.trackClick(
                String(objectID),
                position,
                queryID
              );

              // Save Algolia search params for conversion tracking
              if (
                window.AlgoliaAnalyticsHandlers &&
                window.AlgoliaAnalyticsHandlers.saveAlgoliaSearchParams
              ) {
                window.AlgoliaAnalyticsHandlers.saveAlgoliaSearchParams(
                  String(objectID),
                  queryID
                );
              }
            });

            link.dataset.algoliaTracked = 'true';
          }
        });
      }, 0);
    }

    if (pushGTMViewItemList) {
      pushGTMViewItemList(
        hits,
        true,
        localStorage.getItem('camps_search_session_id'),
        true
      );
    }

    if(selectItemTracking) {
      selectItemTracking(hits, localStorage.getItem("camps_search_session_id"))
    }
  }

  // Render Load More button
  const totalAvailable =
    state.totalHits || state.allFilteredHits.length || nbHits;
  console.log('🔘 Rendering Load More button:', {
    displayed: state.displayedHits,
    total: totalAvailable,
    totalHits: state.totalHits,
  });
  renderLoadMoreButton(state.displayedHits, totalAvailable);

  // Update active filters (only on initial render)
  if (!append && updateActiveFilters) {
    updateActiveFilters();
  }
}

/**
 * Update results heading text
 * @param {Array} hits - Results array
 * @param {number} nbHits - Total count
 * @param {string|null} labelTemplate - Template string
 */
function updateResultsHeading(hits, nbHits, labelTemplate) {
  if (!elements.resultsHeading) return;

  // If labelTemplate is provided (from decision flow step), use it
  if (labelTemplate) {
    let label = labelTemplate.replace('{count}', nbHits || 0);
    // If no location in state, remove location prefix from label template
    if (!state.zip || !state.zip.trim()) {
      label = label.replace(/^[^:]+:\s*/, '');
    }
    // Add sports to label if they exist (use explicit or implicit sport)
    const effectiveSportSlug = (state.sportSlug && state.sportSlug.length > 0)
      ? state.sportSlug
      : (state.implicitSportSlug || []);
    if (effectiveSportSlug.length > 0 && effectiveSportSlug.length <= 2) {
      const sportNames = getSportNamesBySlugs(effectiveSportSlug);
      if (sportNames.length > 0) {
        const sportsLower = sportNames
          .map((s) => (s || '').toLowerCase())
          .filter(Boolean);
        label = label.replace(' camps found', ` ${sportsLower.join(' and ')} camps found`);
      }
    }
    elements.resultsHeading.textContent = label;
  } else {
    // Generate heading based on geocode result
    generateHeadingFromGeocode(nbHits);
  }
}

/**
 * Generate heading text from geocode result
 * @param {number} nbHits - Total count
 */
function generateHeadingFromGeocode(nbHits) {
  let headingText = `${nbHits || 0} camps found`;

  // Embedded mode with preset location: "{count} {sport} Camps in/Near {location}"
  if (state.embeddedPresetLocation) {
    const sport = state.embeddedSportTitle ? `${state.embeddedSportTitle} ` : '';
    const preposition = state.isStateSearch ? 'in' : 'Near';
    const location = state.zip || state.embeddedPresetLocation;
    headingText = `${nbHits || 0} ${sport}Camps ${preposition} ${location}`;

    elements.resultsHeading.textContent = headingText;
    return;
  }

  // Embedded mode with custom value: "{count} {customValue} {sport} camps"
  if (state.embeddedCustomValue || state.embeddedSportTitle) {
    const parts = [nbHits || 0];
    if (state.embeddedCustomValue) parts.push(state.embeddedCustomValue);
    if (state.embeddedSportTitle) parts.push(state.embeddedSportTitle);
    parts.push('camps');
    headingText = parts.join(' ');
    elements.resultsHeading.textContent = headingText;
    return;
  }

  // Title search with multiple results - show search term in quotes
  if (state.geocodeResult?.isTitleSearch) {
    headingText = `"${state.zip}": ${nbHits || 0} camps found`;
    headingText = addSportsToHeading(headingText);
    elements.resultsHeading.textContent = headingText;
    return;
  }

  // Camp was found by title (single result) - show label with location from dual search
  if (state.geocodeResult?.isCampFound) {
    headingText = addSportsToHeading(headingText);
    // Add location from dual search if available
    if (state.dualSearchLocation?.city && state.dualSearchLocation?.state) {
      headingText = `${state.dualSearchLocation.city}, ${state.dualSearchLocation.state}: ${headingText}`;
    }
    elements.resultsHeading.textContent = headingText;
    return;
  }

  // Google API returned a place but camp not found
  if (state.geocodeResult?.isPlace && !state.geocodeResult?.isCampFound) {
    headingText = addSportsToHeading(headingText);
    elements.resultsHeading.textContent = headingText;
    return;
  }

  // Recognized location with corrected label
  if (state.geocodeResult?.recognized && state.geocodeResult.correctedLocationLabel) {
    const correctedLabel = state.geocodeResult.correctedLocationLabel || '';
    const cityMatches = state.geocodeResult?.cityMatches || false;
    const inputIsPartOfResult = state.geocodeResult?.inputIsPartOfResult || false;
    const hasTypos =
      !cityMatches &&
      !!state.zip &&
      !!correctedLabel &&
      hasSpellingMistakes(state.zip, correctedLabel, state.geocodeResult?.correctedCity || '');

    // On typo: show corrected label. On input-is-part-of-result / no error: show user input
    if (inputIsPartOfResult && state.zip?.trim()) {
      headingText = `${state.zip.trim()}: ${nbHits || 0} camps found`;
    } else if (hasTypos && correctedLabel) {
      headingText = `${correctedLabel}: ${nbHits || 0} camps found`;
    } else if (state.zip?.trim()) {
      headingText = `${state.zip.trim()}: ${nbHits || 0} camps found`;
    } else {
      headingText = `${correctedLabel}: ${nbHits || 0} camps found`;
    }

    headingText = addSportsToHeading(headingText);

    elements.resultsHeading.textContent = headingText;
    return;
  }

  // Default: use state.zip (from last Search)
  const canShowQueryPrefix =
    state.zip &&
    state.zip.trim().length > 0 &&
    (!state.geocodeResult || state.geocodeResult.recognized === true);
  const zipSearchValue = canShowQueryPrefix ? state.zip.trim() + ':' : '';
  const effectiveSport = (state.sportSlug && state.sportSlug.length > 0)
    ? state.sportSlug
    : (state.implicitSportSlug || []);
  const sportsLabel =
    effectiveSport.length <= 2
      ? getSportNamesBySlugs(effectiveSport)
          .map((s) => (s || '').toLowerCase())
          .filter(Boolean)
          .join(' and ')
      : '';
  headingText = `${zipSearchValue} ${nbHits || 0} ${sportsLabel} camps found`;

  elements.resultsHeading.textContent = headingText;
}

/**
 * Add sports to heading text (helper)
 * Uses explicit sportSlug or implicit from dual search
 * @param {string} headingText - Current heading
 * @returns {string} - Updated heading with sports
 */
function addSportsToHeading(headingText) {
  const effectiveSportSlug = (state.sportSlug && state.sportSlug.length > 0)
    ? state.sportSlug
    : (state.implicitSportSlug || []);
  if (effectiveSportSlug.length > 0 && effectiveSportSlug.length <= 2) {
    const sportNames = getSportNamesBySlugs(effectiveSportSlug);
    if (sportNames.length > 0) {
      const sportsLower = sportNames
        .map((s) => (s || '').toLowerCase())
        .filter(Boolean);
      headingText = headingText.replace(' camps found', ` ${sportsLower.join(' and ')} camps found`);
    }
  }
  return headingText;
}

console.log('📦 Results renderer module loaded');
